import { useEffect, useState } from "react";

import {
  getAgeFromBirthDate,
  getYearFromDate,
  hrDate,
} from "@king-ict/eupisi-ui/utils";

import { useTypedSelector } from "../../../app/rootReducer";
import AnswerTypeEnum from "../../../models/Questionnaire/AnswerTypeEnum";
import ChildAnswerTypeEnum from "../../../models/Questionnaire/ChildAnswerTypeEnum";
import ParentAnswerTypeEnum from "../../../models/Questionnaire/ParentAnswerTypeEnum";
import QuestionDisplayTypeEnum from "../../../models/Questionnaire/QuestionDisplayTypeEnum";
import QuestionnaireQuestion, {
  getAnswerTypeAndSubType,
  isConditionallyShown,
  isEligibleForAutomaticAnswering,
} from "../../../models/Questionnaire/QuestionnaireQuestion";
import InitialConversationQuestionDataItem from "../../../models/Request/InitialConversationQuestionDataItem";
import Request from "../../../models/Request/Request";
import { RequestDocumentData } from "../../../models/Request/RequestDocumentData";
import { selectInitialConversationQuestionGroupBySort } from "../../../slices/questionnaireSlice";
import { selectConversationDataByQuestionGroupSort } from "../../../slices/requestSlice";
import { useInitialConversationDocuments } from "./useInitialConversationDocuments";

interface Props {
  subStep: number;
}

export const useInitialConversationStep = ({ subStep }: Props) => {
  const request = useTypedSelector((state) => state.request.request);

  const questionGroupSort = subStep + 1;
  const questionGroup = useTypedSelector((state) =>
    selectInitialConversationQuestionGroupBySort(state, questionGroupSort)
  );

  const questionGroupConversationItemsState = useTypedSelector((state) =>
    selectConversationDataByQuestionGroupSort(state, questionGroupSort)
  );

  const [
    questionGroupConversationItems,
    setQuestionGroupConversationItems,
  ] = useState<InitialConversationQuestionDataItem[]>([]);

  const { handleRemoveDocuments } = useInitialConversationDocuments();

  useEffect(() => {
    if (request && questionGroup && questionGroupConversationItemsState) {
      const resolvedItems = resolveConversationItems(
        questionGroup.questions,
        questionGroupConversationItemsState,
        request
      );

      setQuestionGroupConversationItems(resolvedItems);
    }
  }, [questionGroup, questionGroupConversationItemsState, request]);

  const handleSetQuestionGroupConversationData = (
    data:
      | InitialConversationQuestionDataItem[]
      | ((
          prevData: InitialConversationQuestionDataItem[]
        ) => InitialConversationQuestionDataItem[])
  ) => {
    const itemsToResolve =
      typeof data === "function" ? data(questionGroupConversationItems) : data;

    const resolvedItems = resolveConversationItems(
      questionGroup?.questions!,
      itemsToResolve,
      request,
      handleRemoveDocuments
    );

    setQuestionGroupConversationItems(resolvedItems);
  };

  return [
    questionGroup,
    questionGroupConversationItems,
    handleSetQuestionGroupConversationData,
  ] as const;
};

const emptyValue = "-";

const resolveConversationItems = (
  questionGroupQuestions: QuestionnaireQuestion[],
  questionGroupConversationItems: InitialConversationQuestionDataItem[],
  request: Request,
  handleRemoveDocuments?: (documents: RequestDocumentData[]) => void
) => {
  let items: InitialConversationQuestionDataItem[] = [];
  const isRepeatableQuestionSet = questionGroupQuestions.some(
    (question) => question.isRepeatable
  );

  if (isRepeatableQuestionSet && questionGroupConversationItems.length > 0) {
    const maxRepeatableQuestionSort = questionGroupConversationItems.reduce(
      (prev, current) =>
        prev.repeatableSort > current.repeatableSort ? prev : current
    ).repeatableSort;

    for (let i = 0; i <= maxRepeatableQuestionSort; i++) {
      questionGroupQuestions.forEach((q) => {
        const conversationItem = questionGroupConversationItems.find(
          (ci) =>
            ci.questionGroupQuestionId === q.questionGroupQuestionId &&
            ci.repeatableSort === i
        );
        items.push(initializeConversationItem(q, request, conversationItem, i));
      });
    }
  } else {
    questionGroupQuestions.forEach((q) => {
      const conversationItem = questionGroupConversationItems.find(
        (ci) => ci.questionGroupQuestionId === q.questionGroupQuestionId
      );

      if (isConditionallyShown(q)) {
        const parentConversationItem = items.find(
          (ci) =>
            ci.questionGroupQuestionId === q.parentQuestionGroupQuestionId &&
            (+(ci.answerId ?? 0) === q.parentAnswerId ||
              ci.answerIds?.includes(q.parentAnswerId ?? 0))
        );

        if (!parentConversationItem) {
          if (
            q.displayTypeId === QuestionDisplayTypeEnum.Upload &&
            conversationItem &&
            conversationItem.documents &&
            conversationItem.documents.length > 0
          ) {
            handleRemoveDocuments &&
              handleRemoveDocuments(conversationItem.documents);
          }
          return;
        }
      }

      items.push(initializeConversationItem(q, request, conversationItem, 0));
    });
  }

  return items;
};

const initializeConversationItem = (
  question: QuestionnaireQuestion,
  request: Request,
  existingConversationItem?: InitialConversationQuestionDataItem,
  repeatableSort?: number
) => {
  let conversationItem = {
    ...existingConversationItem,
    questionGroupQuestionId: question.questionGroupQuestionId,
    questionName: question.name,
    displayTypeId: question.displayTypeId,
    isRequired: question.isRequired,
    isRepeatable: question.isRepeatable,
    repeatableSort: question.isRepeatable ? repeatableSort : undefined,
    isVisible: question.isVisible,
    parentQuestionGroupQuestionId: question.parentQuestionGroupQuestionId,
    parentAnswerId: question.parentAnswerId,
    answer: isEligibleForAutomaticAnswering(question)
      ? resolveQuestionAnswer(question, request)
      : existingConversationItem?.answer,
  } as InitialConversationQuestionDataItem;

  return conversationItem;
};

const resolveQuestionAnswer = (
  question: QuestionnaireQuestion,
  request: Request
) => {
  const { answerType, answerSubType } = getAnswerTypeAndSubType(question);
  let answer = emptyValue;

  switch (answerType) {
    case AnswerTypeEnum.Parent:
      answer = resolveParentQuestionAnswer(
        answerSubType as ParentAnswerTypeEnum,
        request
      );
      break;
    case AnswerTypeEnum.Child:
      answer = resolveChildQuestionAnswer(
        answerSubType as ChildAnswerTypeEnum,
        request
      );
      break;
  }

  return answer;
};

const resolveParentQuestionAnswer = (
  answerType: ParentAnswerTypeEnum,
  request: Request
) => {
  const applicant = request.applicantParent;
  let answer = emptyValue;

  switch (answerType) {
    case ParentAnswerTypeEnum.Fullname:
      answer = `${applicant.firstName} ${applicant.lastName}`;
      break;
    case ParentAnswerTypeEnum.BirthYear:
      answer = getYearFromDate(applicant.dateOfBirth).toString();
      break;
    case ParentAnswerTypeEnum.Profession:
      answer = applicant.professionName || emptyValue;
      break;
    case ParentAnswerTypeEnum.Qualifications:
      answer = applicant.professionalQualificationName || emptyValue;
      break;
    case ParentAnswerTypeEnum.Mobile:
      answer = applicant.phoneNumber;
      break;
    case ParentAnswerTypeEnum.Email:
      answer = applicant.email;
      break;
  }

  return answer;
};

const resolveChildQuestionAnswer = (
  answerType: ChildAnswerTypeEnum,
  request: Request
) => {
  const child = request.child;
  let answer = emptyValue;

  switch (answerType) {
    case ChildAnswerTypeEnum.Sex:
      answer = child.sexName;
      break;
    case ChildAnswerTypeEnum.Birth:
      answer = hrDate(child.dateOfBirth);
      break;
    case ChildAnswerTypeEnum.Oib:
      answer = child.oib;
      break;
    case ChildAnswerTypeEnum.Age:
      answer = getAgeFromBirthDate(child.dateOfBirth).toString();
      break;
    case ChildAnswerTypeEnum.Address:
      const residence = child.permanentResidence;
      if (residence) {
        const addition = residence.streetNumberAddition
          ? ` ${residence.streetNumberAddition}`
          : "";
        answer = `${residence.street} ${residence.streetNumber}${addition}, ${residence.townName}`;
      }
      break;
  }

  return answer;
};
